import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Users, CheckCircle, MessageCircle, DollarSign, TrendingUp, Shield, Loader2, Key } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await api.get(`/analytics/overview?admin_password=${password}`);
      setAnalytics(response.data);
      setIsAuthenticated(true);
      toast.success('Admin access granted!');
    } catch (error) {
      toast.error('Invalid admin password');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="app-container gradient-bg min-h-screen flex items-center justify-center">
        <div className="w-full max-w-md px-4">
          <div className="glass rounded-3xl p-8 border border-primary/30">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <Shield className="text-primary" size={32} />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">Admin Dashboard</h1>
              <p className="text-gray-400 text-sm">Enter admin password to continue</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-400 mb-1.5 block">Admin Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-secondary/50 border border-white/10 rounded-xl h-12 px-4 text-white placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                  placeholder="Enter password"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold rounded-full transition-all duration-300 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Authenticating...
                  </>
                ) : (
                  <>
                    <Key size={20} />
                    Access Dashboard
                  </>
                )}
              </button>
            </form>

            <button
              onClick={() => navigate('/')}
              className="w-full mt-4 text-gray-400 hover:text-white transition-colors text-sm"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container gradient-bg min-h-screen">
      {/* Header */}
      <div className="bg-primary h-16 flex items-center justify-between px-4 sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <Shield className="text-white" size={24} />
          <span className="text-white font-bold text-xl">Admin Dashboard</span>
        </div>
        <button
          onClick={() => navigate('/')}
          className="text-white text-sm hover:underline"
        >
          Exit
        </button>
      </div>

      <div className="px-4 py-6">
        <h2 className="text-white font-bold text-2xl mb-6">Analytics Overview</h2>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {/* Total Users */}
          <div className="card-enhanced rounded-2xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <Users className="text-primary" size={20} />
              </div>
              <div>
                <p className="text-gray-400 text-xs">Total Users</p>
                <p className="text-white font-bold text-2xl">{analytics?.total_users || 0}</p>
              </div>
            </div>
          </div>

          {/* Verified Users */}
          <div className="card-enhanced rounded-2xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="text-green-400" size={20} />
              </div>
              <div>
                <p className="text-gray-400 text-xs">Verified</p>
                <p className="text-white font-bold text-2xl">{analytics?.verified_users || 0}</p>
              </div>
            </div>
            <p className="text-xs text-gray-500">{analytics?.verification_rate}</p>
          </div>

          {/* Photo Verified */}
          <div className="card-enhanced rounded-2xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Shield className="text-amber-400" size={20} />
              </div>
              <div>
                <p className="text-gray-400 text-xs">Photo Verified</p>
                <p className="text-white font-bold text-2xl">{analytics?.photo_verified_users || 0}</p>
              </div>
            </div>
            <p className="text-xs text-gray-500">{analytics?.photo_verification_rate}</p>
          </div>

          {/* Active Chats */}
          <div className="card-enhanced rounded-2xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                <MessageCircle className="text-purple-400" size={20} />
              </div>
              <div>
                <p className="text-gray-400 text-xs">Active Chats</p>
                <p className="text-white font-bold text-2xl">{analytics?.active_chatrooms || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Stats */}
        <div className="glass rounded-2xl p-6 border border-primary/30 mb-6">
          <h3 className="text-white font-semibold text-lg mb-4">Engagement Metrics</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Total Matches Created</span>
              <span className="text-white font-bold">{analytics?.total_matches_created || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Total Messages Sent</span>
              <span className="text-white font-bold">{analytics?.total_messages || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Recent Signups (7d)</span>
              <span className="text-white font-bold flex items-center gap-1">
                <TrendingUp size={16} className="text-green-400" />
                {analytics?.recent_signups_7d || 0}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Daily Active Users</span>
              <span className="text-white font-bold">{analytics?.daily_active_users || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Total Payments</span>
              <span className="text-white font-bold flex items-center gap-1">
                <DollarSign size={16} className="text-amber-400" />
                {analytics?.total_payments || 0}
              </span>
            </div>
          </div>
        </div>

        {/* System Health */}
        <div className="glass rounded-2xl p-6 border border-green-500/30">
          <h3 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
            <Heart className="text-green-400 fill-green-400" size={20} />
            System Health
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">MongoDB</span>
              <span className="text-green-400 font-semibold">✓ Healthy</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Redis</span>
              <span className="text-green-400 font-semibold">✓ Healthy</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">WebSocket</span>
              <span className="text-green-400 font-semibold">✓ Active</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
